import { ref } from 'vue'

/**
 * Extended vocabulary entry — consumer-defined concept that augments
 * the built-in `ICON_VOCABULARY`. Useful when a consumer app needs
 * domain-specific concepts (e.g. `fingerprint`, `vault`, `qr-code` in
 * a password manager; `wrench`, `gear`, `calendar-alert` in a CMMS)
 * that don't belong in the design-system canonical list.
 */
export interface ExtendedVocabularyEntry {
  /**
   * Per-locale aria-label. Defaults `'en'` when locale lookup fails;
   * follows the same pattern as `ICON_LABELS` for built-in concepts.
   */
  labels: { en: string, tr?: string, [locale: string]: string | undefined }
  /**
   * Per-family icon name overrides. Identity case (concept name = icon
   * name in family) is implicit — omit the entry for families where
   * the concept's name works as-is.
   *
   * Example for `fingerprint`:
   * ```ts
   * {
   *   labels: { en: 'Biometric', tr: 'Biyometrik' },
   *   divergence: {
   *     heroicons: 'finger-print',  // heroicons hyphenates
   *     // lucide, mdi, ph, tabler use identity 'fingerprint'
   *   }
   * }
   * ```
   */
  divergence?: Record<string, string>
}

/**
 * Reactive registry of extended concepts. `Icon.vue` consults this
 * first before falling through to the built-in vocabulary; consumer
 * UIs (ThemeIconPanel, IconBrowser) include extended concepts in
 * their lists automatically.
 *
 * Stored as a Vue ref so `extendVocabulary()` triggers re-render in
 * any consumer subscribed via computed() — theme editor's
 * compatibility report updates live as consumer adds entries.
 */
export const extendedRegistry = ref<Record<string, ExtendedVocabularyEntry>>({})

/**
 * Register additional concepts into the icon vocabulary. Safe to call
 * multiple times — entries with the same key are overwritten, others
 * are merged.
 *
 * Example (consumer app's main.ts):
 * ```ts
 * import { extendVocabulary } from '@elveris/ui/icon'
 *
 * extendVocabulary({
 *   fingerprint: {
 *     labels: { en: 'Biometric', tr: 'Biyometrik' },
 *     divergence: { heroicons: 'finger-print' },
 *   },
 *   vault: {
 *     labels: { en: 'Vault', tr: 'Kasa' },
 *   },
 * })
 * ```
 *
 * After registration, `<Icon name="fingerprint"/>` resolves through
 * the active theme like any built-in concept. TS-side, the consumer
 * loses the IconName literal-union autocomplete for their entries; we
 * recommend declaring a local type alias:
 * ```ts
 * export type PassIconName = IconName | 'fingerprint' | 'vault'
 * ```
 */
export function extendVocabulary(entries: Record<string, ExtendedVocabularyEntry>): void {
  extendedRegistry.value = { ...extendedRegistry.value, ...entries }
}

/**
 * Inverse — remove previously registered entries. Useful for tests
 * and feature-flag toggling. No-op for entries that weren't
 * registered.
 */
export function unextendVocabulary(keys: string[]): void {
  const next = { ...extendedRegistry.value }
  for (const k of keys) delete next[k]
  extendedRegistry.value = next
}

/**
 * Snapshot of currently extended concept names — for tests, telemetry,
 * or designer UI surfaces that want to list extended vs built-in.
 */
export function getExtendedKeys(): string[] {
  return Object.keys(extendedRegistry.value)
}

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

/**
 * Storage adapter interface. Two ship-built implementations:
 *   - `localStorageAdapter()` — browser localStorage
 *   - `sessionStorageAdapter()` — browser sessionStorage
 *
 * Consumer apps backed by a real datastore (Pinia + IndexedDB, server
 * sync, etc.) can provide their own adapter conforming to this shape.
 *
 * `load()` is called once during `installExtendedVocabularyPersistence`
 * to restore prior entries. `save()` is called whenever
 * `extendedRegistry` mutates so the snapshot stays current.
 */
export interface ExtendedVocabularyStorage {
  load: () => Record<string, ExtendedVocabularyEntry> | null
  save: (entries: Record<string, ExtendedVocabularyEntry>) => void
}

const DEFAULT_STORAGE_KEY = 'elveris.icon.extended-vocabulary'

/**
 * Browser localStorage-backed adapter. Survives full page reloads;
 * scope is the origin. Use this when consumer-added concepts should
 * persist across tab restarts (the password-manager use case).
 */
export function localStorageAdapter(key: string = DEFAULT_STORAGE_KEY): ExtendedVocabularyStorage {
  return {
    load() {
      if (typeof localStorage === 'undefined') return null
      try {
        const raw = localStorage.getItem(key)
        if (!raw) return null
        return JSON.parse(raw) as Record<string, ExtendedVocabularyEntry>
      }
      catch {
        return null
      }
    },
    save(entries) {
      if (typeof localStorage === 'undefined') return
      try {
        localStorage.setItem(key, JSON.stringify(entries))
      }
      catch {
        // Quota exceeded / disabled storage — degrade silently.
      }
    },
  }
}

/**
 * sessionStorage variant — survives reload within the tab but not
 * across tab restarts. Use for ephemeral concept sets (e.g. a
 * theme-editor preview that shouldn't leak into production state).
 */
export function sessionStorageAdapter(key: string = DEFAULT_STORAGE_KEY): ExtendedVocabularyStorage {
  return {
    load() {
      if (typeof sessionStorage === 'undefined') return null
      try {
        const raw = sessionStorage.getItem(key)
        if (!raw) return null
        return JSON.parse(raw) as Record<string, ExtendedVocabularyEntry>
      }
      catch {
        return null
      }
    },
    save(entries) {
      if (typeof sessionStorage === 'undefined') return
      try {
        sessionStorage.setItem(key, JSON.stringify(entries))
      }
      catch { /* same degradation as localStorage */ }
    },
  }
}

let uninstallWatcher: (() => void) | null = null

/**
 * Wire an adapter into the registry. Called once during consumer app
 * setup (typically the same place that calls `useIconographyProvider`).
 *
 * - Restores previously saved entries on first call (merged with any
 *   already-registered runtime entries).
 * - Persists every subsequent `extendVocabulary`/`unextendVocabulary`
 *   mutation through the adapter's `save()`.
 *
 * Returns an `uninstall()` callback that detaches the persistence
 * watcher — useful in tests and HMR teardown.
 *
 * Re-installation (calling this function while an installation is
 * already active) auto-uninstalls the previous adapter so consumers
 * can swap storage backends without leaking watchers.
 */
export async function installExtendedVocabularyPersistence(
  storage: ExtendedVocabularyStorage,
): Promise<() => void> {
  if (uninstallWatcher) uninstallWatcher()
  const restored = storage.load()
  if (restored) {
    extendedRegistry.value = { ...restored, ...extendedRegistry.value }
  }
  // Dynamic import so the persistence pathway doesn't pull `watch`
  // into bundles that never call `installExtendedVocabularyPersistence`.
  const { watch } = await import('vue')
  const stop = watch(
    extendedRegistry,
    (next) => storage.save(next),
    { deep: true },
  )
  uninstallWatcher = () => {
    stop()
    uninstallWatcher = null
  }
  return uninstallWatcher
}
